<template>
  <div class="ap-footer" :class="{ 'ap-footer--stacked': stacked }">
    <div v-if="legend.length || ascentRows.length" class="ap-legend">
      <span v-for="swatch in legend" :key="swatch.label" class="ap-legend__item">
        <span class="ap-legend__swatch" :style="{ background: swatch.color }"></span>
        <strong>{{ swatch.label }}</strong> <span class="ap-muted">{{ swatch.value }}</span>
      </span>
      <span v-for="row in ascentRows" :key="row.label" class="ap-legend__item">
        <span class="ap-legend__ramp" :style="{ background: `linear-gradient(90deg, ${row.colors.join(', ')})` }"></span>
        <strong>{{ row.label }}</strong> <span class="ap-muted">{{ row.range }}</span>
      </span>
    </div>
    <p v-if="insight" class="ap-insight">
      <i18n-t v-if="insight.fastest" keypath="recordings.activity.fastest_climbing" tag="span" scope="global">
        <template #rate><strong>{{ insight.rate }}</strong></template>
        <template #range>{{ insight.range }}</template>
        <template #gradient>{{ insight.gradient }}</template>
      </i18n-t>
      <i18n-t v-else keypath="recordings.activity.steepest_section" tag="span" scope="global">
        <template #range><strong>{{ insight.range }}</strong></template>
        <template #gradient>{{ insight.gradient }}</template>
      </i18n-t>
    </p>
    <p v-else-if="cadence" class="ap-insight">
      <i18n-t v-if="cadence.drops" keypath="recordings.activity.cadence_drops" tag="span" scope="global">
        <template #cadence><strong>{{ withUnit(cadence.cadence, $t('recordings.activity.spm')) }}</strong></template>
      </i18n-t>
      <i18n-t v-else keypath="recordings.activity.cadence_rises" tag="span" scope="global">
        <template #cadence><strong>{{ withUnit(cadence.cadence, $t('recordings.activity.spm')) }}</strong></template>
      </i18n-t>
    </p>
  </div>
</template>

<script setup lang="ts">
import type { CadenceInsight, LegendSwatch } from '../../../utils/recordingActivity/activityPanel';
import { withUnit } from '../../../utils/recordingActivity/format';
import type { AscentInsight, AscentLegendRow } from '../../../utils/recordingActivity/recordingActivity';

defineProps<{
  legend: LegendSwatch[];
  ascentRows: AscentLegendRow[];
  insight: AscentInsight | null;
  cadence: CadenceInsight | null;
  /** A phone's width: the insight goes under the legend. */
  stacked?: boolean;
}>();
</script>

<style scoped>
.ap-footer {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 24px;
  padding: 10px 20px;
  font-size: 0.85rem;
  background: rgba(30, 32, 38, 0.8);
}

.ap-footer--stacked {
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 14px;
}

.ap-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
}

.ap-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ap-legend__swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.ap-legend__ramp {
  width: 40px;
  height: 8px;
  border-radius: 4px;
}

.ap-muted {
  color: var(--text-muted, #b2b2b2);
}

.ap-insight {
  margin: 0;
}
</style>
